import { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCampaignSetupDispatch } from '../Context/CampaignSetupContext'
import { useDropzone } from 'react-dropzone'
import { useReducerAsync } from 'hooks/useReducerAsync'
import { actions, asyncActionHandlers, fileStatus, initialState, mediaUploadReducer } from './media'
import { getMediaDimensions } from 'utils/helpers'


// service
export const useMediaUploadService = (props) => {
  const { initialMedia, onChange, canAdd, maxItems } = props
  
  const ref = useRef(null)

  // Initialize group_index for media coming from backend
  const initializeGroupIndex = (mediaArray) => {
    if (!Array.isArray(mediaArray)) return []

    const groupIndexByGroupId = new Map()
    let nextGroupIndex = 0

    return mediaArray.map((file) => {
      // Preserve existing group_index if it's already set
      if (file.group_index !== null && file.group_index !== undefined) {
        return file
      }

      const groupId = file.placement_group_id

      // Standalone file (no group) -> new row
      if (!groupId) {
        const updatedFile = { ...file, group_index: nextGroupIndex }
        nextGroupIndex += 1
        return updatedFile
      }

      // Grouped file -> share same group_index with its group
      let groupIndex = groupIndexByGroupId.get(groupId)
      if (groupIndex === undefined) {
        groupIndex = nextGroupIndex
        groupIndexByGroupId.set(groupId, groupIndex)
        nextGroupIndex += 1
      }

      return { ...file, group_index: groupIndex }
    })
  }

  const [state, dispatch] = useReducerAsync(
    mediaUploadReducer,
    { ...initialState, media: initializeGroupIndex(initialMedia) },
    asyncActionHandlers,
  )
  const { setHasPendingMedia } = useCampaignSetupDispatch()
  const [isMobileSize, setIsMobileSize] = useState(false)
  const [expandedImage, setExpandedImage] = useState(null)
  const { t } = useTranslation()

  const { media } = state

  const canAddInternal = canAdd && media.length < maxItems
  const reachedMaxLimit = media.length >= maxItems

  // Calculate placement_type for files that don't have it (from backend)
  useEffect(() => {
    const calculatePlacementTypes = async () => {
      const filesNeedingPlacementType = media.filter(
        (file) => !file.placement_type && file.url && file.status === fileStatus.success
      )

      if (filesNeedingPlacementType.length === 0) return

      const updates = await Promise.all(
        filesNeedingPlacementType.map(async (file) => {
          try {
            const dimensions = await getMediaDimensions(file)
            const aspectRatio = dimensions.height / dimensions.width
            const placement_type = aspectRatio > 1.4 ? 'reel' : 'feed'
            return { id: file.id, placement_type }
          } catch (error) {
            console.warn('Could not determine placement_type for file:', file.id, error)
            return { id: file.id, placement_type: 'feed' } // default to feed
          }
        })
      )

      // Update files with calculated placement_type
      updates.forEach((update) => {
        dispatch({
          type: actions.editFileInfo,
          payload: { id: update.id, placement_type: update.placement_type },
        })
      })
    }

    calculatePlacementTypes()
  }, [media, dispatch])

  useEffect(() => {
    const _media = media.filter((x) => ![fileStatus.uploading, fileStatus.new].includes(x.status))
    const _uploadingMedia = media.filter((x) =>
      [fileStatus.uploading, fileStatus.new].includes(x.status),
    )

    const hasDiff =
    _media.some((x) => initialMedia.every((m) => m.id !== x.id || m.status !== x.status)) ||
    initialMedia.some((x) => _media.every((m) => m.id !== x.id || m.status !== x.status))
    
    if (hasDiff) {
      const removedIdx = initialMedia.findIndex((x) => !_media.some((m) => m.id === x.id))
      // Pass _media (completed files) instead of media (all files) to ensure placement_group_id is included
      onChange(_media, removedIdx >= 0 ? removedIdx : null, _uploadingMedia.length > 0)
    } else if (_uploadingMedia.length > 0) {
      setHasPendingMedia(true)
    }
  }, [media, setHasPendingMedia])

  useEffect(() => {
    const uploadDiv = ref.current
    const handleDimensionChange = () => {
      if (uploadDiv.offsetWidth < 550) {
        setIsMobileSize(true)
      } else setIsMobileSize(false)
    }

    window.addEventListener('resize', handleDimensionChange)
    return () => window.removeEventListener('resize', handleDimensionChange)
  }, [])

  const validateFile = async (file) => {
    const errorMessages = []

    // Image File Validation
    if (file.type.startsWith('image/')) {
      // Check size (Max 30 MB)
      if (file.size > 30 * 1024 * 1024) {
        errorMessages.push(t('common.errors.fileErrors.imageSize', { fileName: file.name }))
      }

      // Check type (JPG, PNG)
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        errorMessages.push(t('common.errors.fileErrors.imageType', { fileName: file.name }))
      }

      await new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const { width, height } = img
          const aspectRatio = width / height
          const isverticalFormat = aspectRatio >= 0.5 && aspectRatio <= 0.575 && height > width
          // Reel,story image - resolution validation
          if (isverticalFormat && (width > 1080 || height > 1920)) {
            errorMessages.push(
              t('common.errors.fileErrors.imageResolution', { fileName: file.name }),
            )
          }
          resolve()
        }
        img.src = URL.createObjectURL(file)
      })
    }

    // Video File Validation
    if (file.type.startsWith('video/')) {
      // Check size (Max 4 GB for videos)
      if (file.size > 4 * 1024 * 1024 * 1024) {
        errorMessages.push(t('common.errors.fileErrors.videoSize', { fileName: file.name }))
      }

      // Check type (MP4, MOV)
      if (!['video/mp4', 'video/quicktime'].includes(file.type)) {
        errorMessages.push(t('common.errors.fileErrors.videoType', { fileName: file.name }))
      }

      await new Promise((resolve) => {
        const video = document.createElement('video')
        video.onloadedmetadata = () => {
          const { videoWidth, videoHeight } = video
          const aspectRatio = videoWidth / videoHeight
          const isverticalFormat =
            aspectRatio >= 0.5 && aspectRatio <= 0.575 && videoHeight > videoWidth
          // Feed validation
          if (video.duration > 240 * 60) {
            errorMessages.push(
              t('common.errors.fileErrors.videoDurationFeed', { fileName: file.name }),
            )
          }
          // Reel, Story validation
          if (isverticalFormat) {
            if (video.duration > 2 * 60) {
              errorMessages.push(
                t('common.errors.fileErrors.videoDurationReelStory', { fileName: file.name }),
              )
            }
            // if (videoWidth > 1080 || videoHeight > 1920) {
            //   errorMessages.push(
            //     t('common.errors.fileErrors.videoResolution', { fileName: file.name }),
            //   )
            // }
          }

          resolve()
        }
        video.src = URL.createObjectURL(file)
      })
    }
    return errorMessages.length > 0 ? errorMessages : null
  }

  const onDropFile = async (files) => {
    const validFiles = []
    const errorMessages = []
    const isGroupUpload = files.length > 1
    for (const file of files) {
      const validationErrors = await validateFile(file)
      if (validationErrors) {
        errorMessages.push({ file, errors: validationErrors })
      } else {
        validFiles.push(file)
      }
    }
    const hasValidFiles = validFiles.length > 0 ? true : false

    if (errorMessages.length > 0) {
      onChange(media, null, true, errorMessages, hasValidFiles, isGroupUpload)
    }

    if (validFiles.length > 0) {
      dispatch({
        type: actions.addFilesAsync,
        payload: validFiles.slice(0, Math.min(validFiles.length, maxItems - media.length)),
      })
    }
  }

  const removeFile = (fileId) => {
    const file = media.find((x) => x.id === fileId)

    if (file.id === state.fileToReplace?.id) dispatch({ type: actions.resetReplace })
    else dispatch({ type: actions.removeFileAsync, payload: file })
  }

  const removeGroup = (groupId) => {
    dispatch({ type: actions.removeGroupAsync, payload: { groupId } })
  }

  const removeFromGroup = (fileId) => {
    dispatch({ type: actions.removeFromGroupAsync, payload: { fileId } })
  }

  const handleAddFileToGroup = async (file, targetFileId, requiredPlacementType) => {
    await dispatch({
      type: actions.addFileToGroupAsync,
      payload: { file, targetFileId, requiredPlacementType },
    })
  }

  const handleReplaceFileInGroup = async (file, fileToReplaceId, requiredPlacementType) => {
    await dispatch({
      type: actions.replaceFileInGroupAsync,
      payload: { file, fileToReplaceId, requiredPlacementType },
    })
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: onDropFile,
    accept: 'image/jpeg, image/png, video/*',
    multiple: canAddInternal,
    onFileDialogCancel: () => {
      if (state.fileToReplace) dispatch({ type: actions.resetReplace })
    },
  })

  // Get grouped media for navigation (ordered by group_index)
  const getGroupedMediaForNavigation = () => {
    const groups = new Map()

    media.forEach((file) => {
      const groupId = file.placement_group_id || null
      const key = groupId || `single-${file.id}`

      if (!groups.has(key)) {
        groups.set(key, {
          groupId,
          group_index: file.group_index ?? 0,
          files: [],
        })
      }

      const group = groups.get(key)
      group.files.push(file)
      if (file.group_index !== null && file.group_index !== undefined) {
        group.group_index = file.group_index
      }
    })

    const groupedItems = Array.from(groups.values())
      .sort((a, b) => {
        const aIdx = a.group_index ?? 0
        const bIdx = b.group_index ?? 0
        return aIdx - bIdx
      })
      .map((group) => {
        const feedFile = group.files.find((f) => f.placement_type === 'feed') || null
        const reelFile = group.files.find((f) => f.placement_type === 'reel') || null

        return {
          groupId: group.groupId,
          feedFile,
          reelFile,
        }
      })

    return groupedItems
  }

  const onNext = (currentItem) => {
    const groupedMedia = getGroupedMediaForNavigation()
    
    const currentIndex = groupedMedia.findIndex(
      (item) => {
        if (currentItem.groupId && item.groupId) {
          return currentItem.groupId === item.groupId
        }
        
        const feedMatch = item.feedFile?.id === currentItem.id
        const reelMatch = item.reelFile?.id === currentItem.id
        
        return feedMatch || reelMatch
      }
    )
    
    if (currentIndex >= 0 && currentIndex < groupedMedia.length - 1) {
      const nextItem = groupedMedia[currentIndex + 1]
      const displayFile = nextItem.feedFile || nextItem.reelFile
      setExpandedImage({
        ...displayFile,
        groupId: nextItem.groupId,
        feedFile: nextItem.feedFile,
        reelFile: nextItem.reelFile,
      })
    }
  }

  const onBack = (currentItem) => {
    const groupedMedia = getGroupedMediaForNavigation()
    
    const currentIndex = groupedMedia.findIndex(
      (item) => {
        if (currentItem.groupId && item.groupId) {
          return currentItem.groupId === item.groupId
        }

        const feedMatch = item.feedFile?.id === currentItem.id
        const reelMatch = item.reelFile?.id === currentItem.id
        
        return feedMatch || reelMatch
      }
    )
    
    if (currentIndex > 0) {
      const prevItem = groupedMedia[currentIndex - 1]
      const displayFile = prevItem.feedFile || prevItem.reelFile
      setExpandedImage({
        ...displayFile,
        groupId: prevItem.groupId,
        feedFile: prevItem.feedFile,
        reelFile: prevItem.reelFile,
      })
    }
  }

  // Calculate whether the currently expanded item is the first or last group
  const getExpandedPosition = () => {
    if (!expandedImage) {
      return { isFirstItem: false, isLastItem: false }
    }

    const groupedMedia = getGroupedMediaForNavigation()
    
    const currentIndex = groupedMedia.findIndex((item) => {
      if (expandedImage.groupId && item.groupId) {
        return expandedImage.groupId === item.groupId
      }
      
      const feedMatch = item.feedFile?.id === expandedImage.id
      const reelMatch = item.reelFile?.id === expandedImage.id
      
      return feedMatch || reelMatch
    })

    if (currentIndex === -1) {
      return { isFirstItem: false, isLastItem: false }
    }

    return {
      isFirstItem: currentIndex === 0,
      isLastItem: currentIndex === groupedMedia.length - 1,
    }
  }

  const { isFirstItem, isLastItem } = getExpandedPosition()

  // return
  return {
    media,
    canAddInternal,
    reachedMaxLimit,
    isMobileSize,
    expandedImage,
    isFirstItem,
    isLastItem,
    ref,
    state,
    dispatch,
    onNext,
    onBack,
    removeFile,
    removeGroup,
    removeFromGroup,
    handleAddFileToGroup,
    handleReplaceFileInGroup,
    getRootProps,
    getInputProps,
    open,
    setExpandedImage,
  }
}